import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { push } from 'connected-react-router';
import styled from 'styled-components';
import Swal from 'sweetalert2';
import usePreventNavigation from '../../../../hooks/usePreventNavigation';
import RegularProductConfig from '../regular/RegularProductConfig';
import {
  clearOptionalIncentiveToConfigure,
  skipOptionalIncentive,
} from '../../../../actions/v3Order';
import { advanceConfigSlot } from '../../../../reducers/productConfigSlice';
import {
  selectEligibleCartIncentivesForIncentiveConfig,
  selectEligibleOrderIncentivesForIncentiveConfig,
  selectIncentiveProductToConfigure,
  selectIncentivesToConfig,
  selectOptionalIncentiveToConfigure,
  selectOptionalIncentivesEnabledForIncentiveConfig,
  selectOrderKeyForIncentiveConfig,
  selectUnresolvedOptionalIncentiveReAdd,
} from '../../../../selectors';

const INCENTIVE_CONFIG_PATH = '/v3/incentives/config';

const SkipRow = styled.div`
  margin: 8px 0 32px;
  text-align: center;
`;

const ReAddPrompt = styled.div`
  background: #eff7fe;
  border-left: 4px solid #479de6;
  border-radius: 6px;
  box-sizing: border-box;
  color: #23374a;
  margin: 0 auto 24px;
  padding: 14px 16px;
  width: 90%;

  @media (min-width: 992px) {
    margin-left: 0;
    margin-right: 0;
    width: 100%;
  }
`;

const ReAddPromptLabel = styled.div`
  color: #327dbd;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  margin-bottom: 4px;
  text-transform: uppercase;
`;

const ReAddPromptTitle = styled.h2`
  color: #1f2f3d;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.25;
  margin: 0 0 6px;
`;

const ReAddPromptText = styled.p`
  color: #4b5964;
  font-size: 14px;
  line-height: 1.45;
  margin: 0;
`;

const RecoveryStatus = styled.div`
  margin: 32px auto;
  text-align: center;
  width: 90%;
`;

const SkipButton = styled.button`
  background: transparent;
  border: none;
  color: #479de6;
  font-weight: 600;
  padding: 8px;
  &:hover {
    color: #479de6;
    text-decoration: underline;
  }
`;

const SkipHint = styled.div`
  color: #555;
  font-size: 13px;
  margin-top: 4px;
`;

// The "No thanks" affordance shown under the bonus product's config panel.
// Reads what it needs straight from the store: the selectors are memoized, so
// sharing them with IncentivesConfig costs nothing and keeps this component's
// dependencies flat instead of threaded down through props.
const SkipAction = () => {
  const dispatch = useDispatch();
  // The flag of the order that EARNED this bonus (its owner order for a
  // cart-level one), not the active shoot's — a mandatory studio's bonus must
  // never be declinable because another studio's gallery was opened last
  // (ENG-2043 R1/R3).
  const optionalIncentivesEnabled = useSelector(
    selectOptionalIncentivesEnabledForIncentiveConfig,
  );
  const optionalIncentiveToConfigure = useSelector(
    selectOptionalIncentiveToConfigure,
  );
  const incentiveProduct = useSelector(selectIncentiveProductToConfigure);
  const orderKeyForIncentives = useSelector(selectOrderKeyForIncentiveConfig);
  const eligibleOrderIncentives = useSelector(
    selectEligibleOrderIncentivesForIncentiveConfig,
  );
  const eligibleCartIncentives = useSelector(
    selectEligibleCartIncentivesForIncentiveConfig,
  );

  if (!optionalIncentivesEnabled || !incentiveProduct) {
    return null;
  }

  const skipCurrentIncentive = () => {
    // Abandoning a cart re-add: the bonus is already on the ignore lists
    // (V3_ADD_TO_CART is what clears them), so just return to the cart.
    if (optionalIncentiveToConfigure) {
      dispatch(clearOptionalIncentiveToConfigure());
      dispatch(push('/v3/cart'));
      return;
    }

    dispatch(
      skipOptionalIncentive({
        productId: incentiveProduct.id,
        sku: incentiveProduct.sku ?? null,
        incentiveAppliesTo: incentiveProduct.incentive_applies_to,
        orderKey: orderKeyForIncentives,
      }),
    );

    // With another eligible bonus left, the re-render presents it next;
    // otherwise land on the cart, where Available Bonuses offers the re-add.
    const remainingIncentives = [
      ...eligibleOrderIncentives,
      ...eligibleCartIncentives,
    ].filter((product) => product.id !== incentiveProduct.id);
    if (remainingIncentives.length === 0) {
      dispatch(push('/v3/cart'));
    }
  };

  return (
    <SkipRow>
      <SkipButton type="button" onClick={skipCurrentIncentive}>
        No thanks &mdash; I don&apos;t want this free item
      </SkipButton>
      <SkipHint>You can add it back from your cart anytime.</SkipHint>
    </SkipRow>
  );
};

const IncentivesConfig = ({ history }) => {
  // prevent user from navigating away from this page
  usePreventNavigation(history, INCENTIVE_CONFIG_PATH);

  const dispatch = useDispatch();
  // Same per-order flag the skip affordance reads: the promise "you can
  // remove this later" must be made against the order that will hold the
  // bonus, so opening another gallery cannot break it.
  const optionalIncentivesEnabled = useSelector(
    selectOptionalIncentivesEnabledForIncentiveConfig,
  );
  const optionalIncentiveToConfigure = useSelector(
    selectOptionalIncentiveToConfigure,
  );

  const unresolvedOptionalReAdd = useSelector(
    selectUnresolvedOptionalIncentiveReAdd,
  );
  const incentivesToConfig = useSelector(selectIncentivesToConfig);
  const incentiveProduct = useSelector(selectIncentiveProductToConfigure);

  // Announce the bonus item and seed the picker for it. This is the only
  // non-user-driven picker open, so the seeding must happen here — the
  // desktop auto-open in RegularProductConfig is width-gated and mobile has
  // no other entry point once the panel is open.
  //
  // ORDERING: this effect relies on running AFTER RegularProductConfig's
  // product-change effect (which resetConfig()s and clears the incentive
  // flag). React guarantees that — child effects run before parent effects —
  // but if that reset ever moves out of an effect, this seeding must move
  // with it.
  useEffect(() => {
    if (!incentiveProduct) return;

    dispatch(advanceConfigSlot());

    if (!optionalIncentiveToConfigure) {
      Swal.fire(
        'Congratulations',
        optionalIncentivesEnabled
          ? 'You qualify for a free bonus item. Please add your bonus item to your cart. You may need to select a background. You can remove this bonus item from your cart later.'
          : 'You qualify for a free bonus item. Please add your bonus item to your cart. You may need to select a background.',
      );
    }
  }, [
    incentiveProduct,
    dispatch,
    optionalIncentiveToConfigure,
    optionalIncentivesEnabled,
  ]);

  useEffect(() => {
    if (!unresolvedOptionalReAdd) return;

    dispatch(clearOptionalIncentiveToConfigure());
    dispatch(push('/v3/cart'));
  }, [dispatch, unresolvedOptionalReAdd]);

  // The pending re-add must not outlive this configurator (ENG-2043 review
  // F5). It used to be cleared only by "No thanks", the unresolved bounce and
  // the add that consumes it, and it is redux-persisted — so a parent who left
  // by any in-app link (usePreventNavigation blocks only POP) kept a marker
  // that survived reloads and retargeted a later, unrelated bonus add into
  // that earlier order, taking this gallery's images with it.
  //
  // The trigger is the LOCATION leaving this route, not an unmount: app.jsx
  // builds `PrivateRouteWithV3Layout` inside `App#render`, so every App
  // re-render gives the route a new component type and React remounts this
  // subtree mid-flow. The cleanup re-checks the current location for the case
  // where the route change is itself what unmounts us.
  const pendingReAddRef = useRef(optionalIncentiveToConfigure);
  useEffect(() => {
    pendingReAddRef.current = optionalIncentiveToConfigure;
  }, [optionalIncentiveToConfigure]);

  useEffect(() => {
    const clearPendingReAddIfLeaving = (location) => {
      if (!pendingReAddRef.current) return;
      if (location?.pathname === INCENTIVE_CONFIG_PATH) return;

      dispatch(clearOptionalIncentiveToConfigure());
    };

    const unlisten = history.listen(clearPendingReAddIfLeaving);

    return () => {
      unlisten();
      clearPendingReAddIfLeaving(history.location);
    };
  }, [dispatch, history]);

  if (unresolvedOptionalReAdd) {
    return (
      <RecoveryStatus role="status">Returning to your cart…</RecoveryStatus>
    );
  }

  // if no incentives to config, push to offerings
  if (incentivesToConfig && incentivesToConfig.length === 0) {
    // history.push('/v3/offers'); // TODO: revisit -- we'll need to address usePreventNavigation is we event want to use this
    return null;
  }

  // TODO: log error here and show generic error message
  if (!incentiveProduct) {
    return (
      <div>
        <p>There was an error</p>
        <p>Error code: E211 - no incentive product to display</p>
      </div>
    );
  }

  return (
    <>
      {optionalIncentiveToConfigure && (
        <ReAddPrompt
          aria-labelledby="optional-incentive-readd-title"
          role="status"
        >
          <ReAddPromptLabel>Next step</ReAddPromptLabel>
          <ReAddPromptTitle id="optional-incentive-readd-title">
            Configure your free item
          </ReAddPromptTitle>
          <ReAddPromptText>
            Review any required options, then select Add to Cart.
          </ReAddPromptText>
        </ReAddPrompt>
      )}
      <RegularProductConfig
        actionFooter={<SkipAction />}
        product={incentiveProduct}
      />
    </>
  );
};

IncentivesConfig.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
    block: PropTypes.func.isRequired,
    // Route changes away from here are what spend a pending re-add marker.
    listen: PropTypes.func.isRequired,
    location: PropTypes.shape({ pathname: PropTypes.string }),
  }).isRequired,
};

export default IncentivesConfig;
